import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/hooks/useAuth';
import { PageContainer } from '@/components/common/PageContainer';
import { DataTable, type Column } from '@/components/common/DataTable';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Building2, Filter, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { NodeEntity } from '@/types/node.types';
import { AddFCModal } from '@/components/features/fulfillment-centers/AddFCModal';
import { EditFCModal } from '@/components/features/fulfillment-centers/EditFCModal';
import { FCFilterSidebar, FCFilterValues } from '@/components/features/fulfillment-centers/FCFilterSidebar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const initialFilters: FCFilterValues = {
  search: '',
  status: '',
  city: '',
};

export default function FulfillmentCentersList() {
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nodes, setNodes] = useState<NodeEntity[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState<NodeEntity | null>(null);
  const [filters, setFilters] = useState<FCFilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);

  const carrierId = user?.carrier_id || '';

  const fetchNodes = useCallback(async () => {
    try {
      const params: Record<string, string> = { type: 'fulfillment_center,hub' };
      if (carrierId) params.carrier_id = carrierId;
      const result: any = await api.get('/api/v1/dashboard/nodes', params);
      const list = result?.data ?? result ?? [];
      setNodes(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error('فشل في جلب مراكز الفرز');
    } finally {
      setHasFetched(true);
    }
  }, [carrierId]);

  useEffect(() => {
    fetchNodes();
  }, [fetchNodes]);

  const handleEdit = (node: NodeEntity) => {
    setSelectedNode(node);
    setShowEditModal(true);
  };

  const handleFilterChange = (key: keyof FCFilterValues, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  // Client-side filtering
  const filteredNodes = nodes.filter((node) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const name = (node.name || '').toLowerCase();
      const code = (node.code || '').toLowerCase();
      if (!name.includes(q) && !code.includes(q)) return false;
    }
    if (filters.status) {
      const isActive = filters.status === 'true';
      if (node.is_active !== isActive) return false;
    }
    if (filters.city) {
      const cityName =
        node.city?.name_translations?.ar || node.city_name?.ar || '';
      if (!cityName.toLowerCase().includes(filters.city.toLowerCase()) &&
          String(node.city?.id || '') !== filters.city) return false;
    }
    return true;
  });

  const columns: Column<NodeEntity>[] = [
    {
      header: 'كود',
      accessor: (row) => (
        <span className="font-mono text-sm">{row.code}</span>
      ),
    },
    {
      header: 'الاسم',
      accessor: (row) => (
        <span className="font-medium">{row.name}</span>
      ),
    },
    {
      header: 'المدينة',
      accessor: (row) => row.city?.name_translations?.ar || row.city_name?.ar || '-',
    },
    {
      header: 'الهاتف',
      accessor: (row) => (
        <span dir="ltr" className="text-sm">
          {row.phone || '-'}
        </span>
      ),
    },
    {
      header: 'النوع',
      accessor: (row) => (
        <Badge variant="outline" className="text-xs">
          {row.type === 'hub' ? 'مركز لوجستي' : 'مركز فرز'}
        </Badge>
      ),
    },
    {
      header: 'الحالة',
      accessor: (row) => (
        <Badge variant={row.is_active ? 'default' : 'secondary'}>
          {row.is_active ? 'نشط' : 'غير نشط'}
        </Badge>
      ),
    },
  ];

  const renderActions = (node: NodeEntity) => (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      onClick={() => handleEdit(node)}
      title="تعديل"
    >
      <Pencil className="h-4 w-4" />
    </Button>
  );

  const FilterSidebarContent = (
    <FCFilterSidebar
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">مراكز الفرز</h1>
              <p className="text-sm text-muted-foreground">إدارة مراكز الفرز والتوزيع</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">{FilterSidebarContent}</div>
              </SheetContent>
            </Sheet>
            <Button onClick={() => setShowAddModal(true)} className="gap-2" disabled={!carrierId}>
              <Plus className="h-4 w-4" />
              إضافة مركز فرز
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex gap-4 ">
        <div className="hidden lg:block">
          {FilterSidebarContent}
        </div>
        <div className="flex-1 min-w-0">
          {!hasFetched ? (
            <LoadingSkeleton type="table" rows={8} />
          ) : filteredNodes.length === 0 ? (
            <EmptyState
              icon={<Building2 className="h-16 w-16" />}
              title="لا توجد مراكز فرز"
              description="أضف مركز الفرز الأول للبدء في العمليات"
              action={{ label: 'إضافة مركز فرز', onClick: () => setShowAddModal(true) }}
            />
          ) : (
            <DataTable
              data={filteredNodes}
              columns={columns}
              actions={renderActions}
              selectable={true}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
            />
          )}
        </div>
      </div>

      <AddFCModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        carrierId={carrierId}
        onSuccess={fetchNodes}
      />

      {selectedNode && (
        <EditFCModal
          open={showEditModal}
          onOpenChange={(open) => {
            setShowEditModal(open);
            if (!open) setSelectedNode(null);
          }}
          node={selectedNode}
          onSuccess={fetchNodes}
        />
      )}
    </PageContainer>
  );
}
